import { useEffect, useRef, useState } from 'react';
import { GhostHand } from './Tutorial.js';

/**
 * Screenshot builds. A real drag is invisible in a still image - the finger is
 * not in the frame and the selection is deliberately faint - so demo mode gives
 * ordinary play the tutorial's heavier box and puts the hand on the pointer.
 * Never ship this: it is a build flag, and the constant folds away without it.
 */
const DEMO = process.env.REACT_APP_DEMO === 'true';

/**
 * iOS decides for itself what a touch-and-hold-and-drag means, and one of its
 * answers is the text-selection gesture - which surfaces the Copy/Share callout
 * over the board mid-drag. CSS alone does not stop it: user-select and
 * touch-callout are already none everywhere, and the callout still arrives,
 * because the gesture is the browser's default action for the touch rather than
 * a property of the element. Cancelling that default is the fix, and is the
 * reason these listeners are registered passive: false.
 *
 * Not over real controls, though - preventDefault on a button's pointerdown
 * swallows the click that follows it.
 */
const CONTROLS = 'button, select, a, input, textarea, label';
const isControl = (target) => Boolean(target?.closest?.(CONTROLS));


function computeTopLeft(pos0, pos1) {
  return {
    x: Math.min(pos0?.x, pos1?.x) ?? 0,
    y: Math.min(pos0?.y, pos1?.y) ?? 0,
  };
}

function computeBottomRight(pos0, pos1) {
  return  {
    x: Math.max(pos0?.x, pos1?.x) ?? 0,
    y: Math.max(pos0?.y, pos1?.y) ?? 0,
  }
}

export default function GlobalClickListener({ onSelect, onRelease, onCancel = () => {} }) {

  const [startPosition, setStartPosition] = useState(null);
  const [endPosition, setEndPosition] = useState(null);
  const [pressed, setPressed] = useState(false);
  // The finger that started the drag. A phone gets more contacts than the one
  // the player means - a second thumb, a palm along the edge - and every one of
  // them used to move the anchor, and to end the drag when it lifted.
  const activePointer = useRef(null);

  useEffect(() => {
    const handlePress = (e) => {
      // Only the first contact of a gesture starts a drag; the rest are noise.
      // A fresh primary always takes over, so a pointerup lost to a backgrounded
      // app cannot leave the board permanently unresponsive.
      // Explicitly false, not falsy: a real PointerEvent always carries this,
      // and anything that does not is a synthetic or legacy event which should
      // still be able to play the game.
      if (e.isPrimary === false) return;
      if (e.target.tagName === 'SELECT' || e.target.closest?.('select')) {
        return; // ignore pointerdown on select
      }
      // Before any state, so the gesture is claimed even if this press turns
      // out not to select anything
      if (!isControl(e.target)) e.preventDefault();
      activePointer.current = e.pointerId;
      setStartPosition({x: e.pageX, y: e.pageY});
      setPressed(true)
    };

    window.addEventListener('pointerdown', handlePress, { passive: false });

    return () => {
      window.removeEventListener('pointerdown', handlePress, { passive: false });
    };
  }, []);

  useEffect(() => {
    const isDragging = (e) => pressed && activePointer.current === e.pointerId;

    const forget = () => {
      activePointer.current = null;
      setPressed(false)
      setEndPosition(null)
      setStartPosition(null);
    };

    const handleMove = (e) => {
      if (!isDragging(e)) return; // Only update end position if pressed
      // A selection that survived the press gets extended by the move, so the
      // drag has to keep saying no for as long as it lasts
      e.preventDefault();
      const endPos = {x: e.pageX, y: e.pageY}
      onSelect({
        p0: computeTopLeft(startPosition, endPos),
        p1: computeBottomRight(startPosition, endPos),
      });
      setEndPosition(endPos);
    }

    const handleRelease = (e) => {
      if (!isDragging(e)) return;
      const endPos = {x: e.pageX, y: e.pageY}
      const selection = {
        p0: computeTopLeft(startPosition, endPos),
        p1: computeBottomRight(startPosition, endPos),
      };
      forget();
      onRelease(selection);
    };

    // Cancelled is not released: the system took the gesture away, or the touch
    // was rejected as a palm. Nobody asked for a move, so nobody gets one - this
    // used to submit whatever rectangle happened to be spanned at the time.
    const handleCancel = (e) => {
      if (!isDragging(e)) return;
      forget();
      onCancel();
    };

    window.addEventListener('pointerup', handleRelease, { passive: false });
    window.addEventListener('pointercancel', handleCancel, { passive: false });
    window.addEventListener('pointermove', handleMove, { passive: false });

    return () => {
      window.removeEventListener('pointerup', handleRelease, { passive: false });
      window.removeEventListener('pointercancel', handleCancel, { passive: false });
      window.removeEventListener('pointermove', handleMove, { passive: false });
    };
  }, [pressed, startPosition, onRelease, onSelect, onCancel]);

  const p0 = computeTopLeft(startPosition, endPosition);
  const p1 = computeBottomRight(startPosition, endPosition);

  if (!(pressed && startPosition && endPosition)) return null;

  // Otherwise this is just a listener, no DOM
  return (
    <>
      <div
        className={'select-box' + (DEMO ? ' demo' : '')}
        style={{ position: 'fixed',
          left: p0.x,
          top: p0.y,
          width: p1.x - p0.x,
          height: p1.y - p0.y,
        }}
      />
      {/* On the finger's own position rather than a corner of the rectangle -
          a drag can be made in any direction, and the hand belongs where the
          hand is */}
      {DEMO && (
        <GhostHand className="demo-hand" style={{ left: endPosition.x, top: endPosition.y }} />
      )}
    </>
  );
}
